<template>
    <div
        ref="cellRef"
        class="ds-month-cell"
        :class="{
            'ds-month-cell-interactive': hasDetails || mode === 'add',
            'ds-month-cell-active': isActive,
            'ds-month-cell-today': isToday,
        }"
        :style="mode === 'dayState' ? { backgroundColor: dayStateColor } : undefined"
        :data-schedule-date="date"
        :tabindex="hasDetails ? 0 : undefined"
        :aria-describedby="isTooltipOpen ? tooltipId : undefined"
        @mouseenter="onPointerEnter"
        @mouseleave="onPointerLeave"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.esc="onEscape"
    >
        <div v-if="stripe" class="ds-month-cell-stripe" :class="STRIPE_CLASSES[stripe]" />

        <div v-if="isMixed && mainShift && externalView" class="ds-month-cell-body ds-month-cell-body-mixed">
            <span class="ds-month-pair">
                <span :class="{ 'ds-month-time-violation': isActive && mainShift.startViolated }">{{ startText(mainShift) }}</span>
                <span :class="{ 'ds-month-time-violation': isActive && mainShift.endViolated }">{{ endText(mainShift) }}<sup v-if="endNextDay(mainShift)" class="ds-month-next-day">+1</sup></span>
            </span>
            <span class="ds-month-pair ds-month-pair-external" data-external-shift>
                <template v-if="externalView.plan">
                    <span>{{ externalView.plan.start }}</span>
                    <span>{{ externalView.plan.end }}<sup v-if="externalView.plan.nextDay" class="ds-month-next-day">+{{ externalView.plan.nextDay }}</sup></span>
                </template>
                <span v-else>—</span>
            </span>
            <span v-if="extraShifts.length" class="ds-month-extra-badge">+{{ extraShifts.length }} доп.</span>
            <span v-else class="ds-month-hours ds-month-hours-compact" :class="{ 'ds-month-hours-muted': totalHours.inProgress }">{{ totalHours.text }}</span>
        </div>

        <div v-else-if="mode === 'external' && externalView" class="ds-month-cell-body ds-month-cell-body-external">
            <span class="ds-month-pair ds-month-pair-external" data-external-shift>
                <template v-if="externalView.plan">
                    <span>{{ externalView.plan.start }}</span>
                    <span>{{ externalView.plan.end }}<sup v-if="externalView.plan.nextDay" class="ds-month-next-day">+{{ externalView.plan.nextDay }}</sup></span>
                </template>
                <span v-else>—</span>
            </span>
        </div>

        <div v-else-if="mode === 'single' && mainShift" class="ds-month-cell-body" :class="{ 'ds-month-cell-body-fact': isActive }">
            <span v-if="isActive" class="ds-month-fact-caption">факт</span>
            <span class="ds-month-time"><span class="ds-month-time-value" :class="{ 'ds-month-time-violation': isActive && mainShift.startViolated }">{{ startText(mainShift) }}</span></span>
            <span class="ds-month-time"><span class="ds-month-time-value" :class="{ 'ds-month-time-violation': isActive && mainShift.endViolated }">{{ endText(mainShift) }}</span><sup v-if="endNextDay(mainShift)" class="ds-month-next-day">+1</sup></span>
            <span class="ds-month-hours" :class="{ 'ds-month-hours-muted': mainShift.isInProgress }">{{ mainShift.hoursText }}</span>
        </div>

        <div v-else-if="mode === 'multi' && mainShift" class="ds-month-cell-body ds-month-cell-body-compact">
            <span class="ds-month-pair">
                <span :class="{ 'ds-month-time-violation': isActive && mainShift.startViolated }">{{ startText(mainShift) }}</span>
                <span :class="{ 'ds-month-time-violation': isActive && mainShift.endViolated }">{{ endText(mainShift) }}<sup v-if="endNextDay(mainShift)" class="ds-month-next-day">+1</sup></span>
            </span>
            <span v-if="extraShifts.length === 1" class="ds-month-pair ds-month-pair-extra">
                <span :class="{ 'ds-month-time-violation': isActive && extraShifts[0].startViolated }">{{ startText(extraShifts[0]) }}</span>
                <span :class="{ 'ds-month-time-violation': isActive && extraShifts[0].endViolated }">{{ endText(extraShifts[0]) }}<sup v-if="endNextDay(extraShifts[0])" class="ds-month-next-day">+1</sup></span>
            </span>
            <span v-else class="ds-month-pair ds-month-pair-extra ds-month-extra-count">
                <span>+{{ extraShifts.length }}</span>
                <span class="ds-month-extra-count-caption">доп.</span>
            </span>
            <span class="ds-month-hours ds-month-hours-compact" :class="{ 'ds-month-hours-muted': totalHours.inProgress }">{{ totalHours.text }}</span>
        </div>

        <template v-else-if="mode === 'dayState'">
            <div v-if="mainShift || externalView" class="ds-month-cell-body ds-month-cell-body-compact ds-month-cell-body-day-state" :class="{ 'ds-month-cell-body-mixed': mainShift && externalView }">
                <!-- RD-185: часы КС — в строке подписи КС, отдельно от лилового блока и часов рабочих смен. -->
                <span v-if="dayStateDuration" class="ds-month-day-state-line">
                    <span class="ds-month-day-state-caption">{{ dayStateShortName }}</span>
                    <span class="ds-month-day-state-hours" data-day-state-duration>{{ dayStateDuration }}</span>
                </span>
                <span v-else class="ds-month-day-state-caption">{{ dayStateShortName }}</span>
                <span v-if="mainShift" class="ds-month-pair ds-month-pair-extra">
                    <span :class="{ 'ds-month-time-violation': isActive && mainShift.startViolated }">{{ startText(mainShift) }}</span>
                    <span :class="{ 'ds-month-time-violation': isActive && mainShift.endViolated }">{{ endText(mainShift) }}<sup v-if="endNextDay(mainShift)" class="ds-month-next-day">+1</sup></span>
                </span>
                <span v-if="externalView" class="ds-month-pair ds-month-pair-external" data-external-shift>
                    <template v-if="externalView.plan">
                        <span>{{ externalView.plan.start }}</span>
                        <span>{{ externalView.plan.end }}<sup v-if="externalView.plan.nextDay" class="ds-month-next-day">+{{ externalView.plan.nextDay }}</sup></span>
                    </template>
                    <span v-else>—</span>
                </span>
                <!-- КС + своя + внешняя не помещаются в 64 px с часами: собственные часы — в подсказке. -->
                <span v-if="mainShift && !externalView" class="ds-month-hours ds-month-hours-compact" :class="{ 'ds-month-hours-muted': totalHours.inProgress }">{{ totalHours.text }}</span>
            </div>
            <!-- RD-185: только КС — часы внизу, в позиции часов обычного дня. -->
            <div v-else-if="dayStateDuration" class="ds-month-cell-body">
                <span class="ds-month-day-state-name">{{ dayStateShortName }}</span>
                <span class="ds-month-hours" data-day-state-duration>{{ dayStateDuration }}</span>
            </div>
            <div v-else class="ds-month-cell-label ds-month-day-state-title">{{ dayStateShortName }}</div>
        </template>

        <div v-else-if="mode === 'dayoff'" class="ds-month-cell-label">{{ dayoffLabel }}</div>

        <div v-else-if="mode === 'add'" class="ds-month-cell-add">
            <icon width="14" height="14" :vb-size="21"><IconPlus /></icon>
        </div>

        <div v-if="isToday" class="ds-week-cell-today" aria-hidden="true" />

        <MonthShiftTooltip
            v-if="isTooltipOpen && cellRef"
            :id="tooltipId"
            :anchor="cellRef"
            :title="tooltipTitle"
            :shifts="shiftViews"
            :day-state="mode === 'dayState' ? { name: dayStateName, color: dayStateColor, duration: dayStateDuration } : null"
            :external-shifts="externalViews"
            :total="shiftViews.length > 1 ? totalHours.known : ''"
        />
    </div>
</template>

<script lang="ts">
import { shallowRef } from 'vue'

// Одновременно открыта одна подсказка месяца: новая ячейка закрывает прежнюю.
const activeTooltipOwner = shallowRef<symbol | null>(null)
let tooltipSequence = 0
</script>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import dayjs from 'dayjs'
import type { Schedule, Shift } from '@/types'
import IconPlus from '@/ui/icons/IconPlus.vue'
import MonthShiftTooltip from './MonthShiftTooltip.vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useAuthStore } from '@/stores/authStore'
import { formatDecimalHours } from '@/utils/duration'
import { buildExternalShiftView, ownRowShifts, rowExternalShifts } from '@/utils/otherDepartmentShifts'
import {
    DATE_FORMAT,
    describeShiftTiming,
    formatDayStateDuration,
    isDayStateDurationShown,
    useWallClockNow,
    type MonthShiftView,
} from './weekSchedule'

interface Props {
    schedule: Schedule
    date: string
}

type CellMode = 'blank' | 'add' | 'dayoff' | 'dayState' | 'single' | 'multi' | 'external'

const TOOLTIP_DELAY = 500
// Полные имена классов: Tailwind вырезает из @layer components классы, собранные шаблонной строкой.
const STRIPE_CLASSES = { 'warning': 'ds-month-cell-stripe-warning', 'no-marks': 'ds-month-cell-stripe-no-marks' } as const

const { schedule, date } = defineProps<Props>()

const scheduleStore = useScheduleStore()
const departmentStore = useDepartmentStore()
const authStore = useAuthStore()

const cellRef = ref<HTMLElement | null>(null)
const owner = Symbol('month-tooltip')
const tooltipId = `month-shift-tooltip-${++tooltipSequence}`
const isHovered = ref(false)
const isFocused = ref(false)
let tooltipTimer: ReturnType<typeof setTimeout> | undefined

const day = computed(() => dayjs(date))
const isToday = computed(() => date === dayjs().format(DATE_FORMAT))
// Общий минутный тикер недели: «идёт», «Нет отметок» и будущая смена актуализируются без перезагрузки.
const now = useWallClockNow()

const notHired = computed(() => dayjs(schedule.hired_at).isAfter(day.value, 'day') || dayjs(schedule.fired_at).isBefore(day.value, 'day'))

const currentDepartmentId = computed(() => scheduleStore.currentDepartment?.id)
// RD-182: собственные смены строки за точную дату — её ТУ в выбранном подразделении. Смены другого
// магазина и другого ТУ сюда не попадают: они не участвуют в раскладке, часах, статусах и «+N доп.».
const shifts = computed(() => ownRowShifts(schedule, currentDepartmentId.value)
    .filter(shift => dayjs(shift.date).isSame(day.value, 'day')))
// Внешняя занятость — только в строке собственного ТУ; по сценарию в день не больше одной (spec r3 п.2),
// в ячейке показывается первая по плановому началу, в подсказке — все без усечения данных.
const externalViews = computed(() => rowExternalShifts(schedule, currentDepartmentId.value, date)
    .map(shift => buildExternalShiftView(shift, departmentStore.departments, scheduleStore.getPosition(shift.position)?.name ?? '')))
const externalView = computed(() => externalViews.value[0])

const dayStateShift = computed(() => shifts.value.find(shift => Boolean(shift.day_state_document)))
// Основная смена первой, дополнительные — по существующей классификации isExtraShift, не по цвету.
const workShifts = computed(() => shifts.value
    .filter(shift => !shift.day_state_document && shift.plan_work_start_at && shift.plan_work_end_at)
    .sort((a, b) => Number(scheduleStore.isExtraShift(a)) - Number(scheduleStore.isExtraShift(b))))

const firstDayType = computed(() => scheduleStore.getDayType((dayStateShift.value ?? shifts.value[0])?.day_type ?? ''))

const mode = computed<CellMode>(() => {
    if (notHired.value) return 'blank'
    if (dayStateShift.value) return 'dayState'
    if (workShifts.value.length > 1) return 'multi'
    if (workShifts.value.length === 1) return 'single'
    // Только внешняя смена: информационный блок без собственных часов; редактор по-прежнему не открывается.
    if (externalView.value) return 'external'
    if (shifts.value.length) return 'dayoff'
    if (schedule.is_vacancy || schedule.department.id !== currentDepartmentId.value) return 'blank'
    // Как раньше: «+» только для будущих дней, прошедшие и сегодняшний без смен — «ВЫХ».
    return day.value.isAfter(dayjs(), 'day') ? 'add' : 'dayoff'
})

const formatHours = (hours: number) => formatDecimalHours(hours, 'schedule')

function buildShiftView(shift: Shift): MonthShiftView {
    const timing = describeShiftTiming(shift, now.value, shift.id ? scheduleStore.scheduleShiftHours.get(shift.id) : undefined)
    let hoursText = ''
    if (timing.isInProgress) hoursText = 'идёт'
    else if (timing.factHoursValue !== null) hoursText = formatHours(timing.factHoursValue)

    return {
        ...timing,
        // Месяц: незавершённый факт — «…» и после планового окончания (неделя оставляет «—», как в RD-137).
        factEnd: timing.isFactOpen ? '…' : timing.factEnd,
        key: shift.id ?? `${shift.employment}-${shift.plan_work_start_at}`,
        isExtra: Boolean(scheduleStore.isExtraShift(shift)),
        positionName: scheduleStore.getPosition(shift.position)?.name ?? schedule.position?.name ?? '',
        planEndDate: timing.planNextDay && timing.planEndAt ? timing.planEndAt.format('dd, DD.MM') : '',
        factEndDate: timing.factNextDay && timing.factEndAt ? timing.factEndAt.format('dd, DD.MM') : '',
        hoursText,
    }
}

const shiftViews = computed(() => workShifts.value.map(buildShiftView))
const mainShift = computed(() => shiftViews.value[0])
const extraShifts = computed(() => shiftViews.value.slice(1))

// Сумма — только если известны часы всех смен дня: будущая, идущая или неизвестная — суммы нет.
const totalHours = computed(() => {
    const views = shiftViews.value
    const inProgress = views.some(view => view.isInProgress)
    const values = views.map(view => view.factHoursValue)
    const known = !inProgress && values.length && values.every(value => value !== null)
        ? formatHours(values.reduce<number>((sum, value) => sum + (value ?? 0), 0))
        : ''
    return { inProgress, known, text: inProgress ? 'идёт' : known }
})

const stripe = computed<'warning' | 'no-marks' | undefined>(() => {
    if (!shiftViews.value.length) return undefined
    if (shiftViews.value.some(view => view.violationCount || view.hasGeneralViolation)) return 'warning'
    if (shiftViews.value.some(view => view.isNoMarks)) return 'no-marks'
    return undefined
})

// Кадровое состояние: DayType.short_name и цвет из справочника (палитра в админке), без распознавания по имени/коду.
const dayStateName = computed(() => firstDayType.value?.name || 'Кадровое отклонение')
const dayStateShortName = computed(() => firstDayType.value?.short_name?.trim() || dayStateName.value.trim().slice(0, 3))
const dayStateColor = computed(() => {
    const color = firstDayType.value?.color?.trim()
    return color && CSS.supports('color', color) ? color : 'var(--color-day-state-bg)'
})

const dayoffLabel = computed(() => (shifts.value.length && firstDayType.value?.short_name?.trim()) || 'ВЫХ')

// RD-185: готовое duration КС (Ч:ММ, неизвестное — «—») при DISPLAY_SHIFT_TIME_IN_CELL: true; иначе пусто
// и разметка КС прежняя. В «Итого»/часы рабочих смен не входит.
const dayStateDuration = computed(() => (
    dayStateShift.value && isDayStateDurationShown(authStore.settings) ? formatDayStateDuration(dayStateShift.value) : ''
))

// Своя + внешняя: свой основной план, внешний на штриховке, внизу собственные часы либо «+N доп.».
const isMixed = computed(() => Boolean(externalView.value && mainShift.value && (mode.value === 'single' || mode.value === 'multi')))

const hasDetails = computed(() => mode.value === 'single' || mode.value === 'multi' || mode.value === 'dayState' || mode.value === 'external')
// Наведение и фокус только меняют отображение тех же данных ячейки: план → факт.
const isActive = computed(() => hasDetails.value && (isHovered.value || isFocused.value))
const isTooltipOpen = computed(() => hasDetails.value && activeTooltipOwner.value === owner)

const tooltipTitle = computed(() => {
    const title = day.value.format('dd, D MMMM')
    const text = title.charAt(0).toUpperCase() + title.slice(1)
    return shiftViews.value.length > 2 ? `${text} · ${shiftViews.value.length} смены` : text
})

const startText = (view: MonthShiftView) => isActive.value ? (view.factStart || '—') : view.planStart
const endText = (view: MonthShiftView) => isActive.value ? (view.factEnd || '—') : view.planEnd
const endNextDay = (view: MonthShiftView) => isActive.value ? view.factNextDay : view.planNextDay

function clearTooltipTimer() {
    if (tooltipTimer) clearTimeout(tooltipTimer)
    tooltipTimer = undefined
}

function openTooltip() {
    clearTooltipTimer()
    if (hasDetails.value) activeTooltipOwner.value = owner
}

function closeTooltip() {
    clearTooltipTimer()
    if (activeTooltipOwner.value === owner) activeTooltipOwner.value = null
}

function onPointerEnter() {
    isHovered.value = true
    if (!hasDetails.value) return
    clearTooltipTimer()
    tooltipTimer = setTimeout(openTooltip, TOOLTIP_DELAY)
}

function onPointerLeave() {
    isHovered.value = false
    if (!isFocused.value) closeTooltip()
}

// Клавиатурный фокус сразу показывает подсказку; фокус от клика мыши её не открывает.
function onFocus(event: FocusEvent) {
    isFocused.value = true
    const target = event.currentTarget
    if (target instanceof HTMLElement && target.matches(':focus-visible')) openTooltip()
}

function onBlur() {
    isFocused.value = false
    if (!isHovered.value) closeTooltip()
}

function onEscape(event: KeyboardEvent) {
    if (!isTooltipOpen.value) return
    // Первое Escape закрывает подсказку, не сбрасывая выделение таблицы.
    event.stopPropagation()
    closeTooltip()
}

// Подсказка не переживает прокрутку/изменение размеров: её позиция привязана к ячейке.
function onViewportChange() {
    closeTooltip()
}

watch(isTooltipOpen, (open) => {
    if (open) {
        window.addEventListener('scroll', onViewportChange, true)
        window.addEventListener('resize', onViewportChange)
    } else {
        window.removeEventListener('scroll', onViewportChange, true)
        window.removeEventListener('resize', onViewportChange)
    }
})

// Ячейка без подробностей (после обновления данных) не держит прежнюю подсказку.
watch(hasDetails, (value) => {
    if (!value) closeTooltip()
})

onUnmounted(() => {
    closeTooltip()
    window.removeEventListener('scroll', onViewportChange, true)
    window.removeEventListener('resize', onViewportChange)
})
</script>
